import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateService } from "@factory/query/service.query";
import { ServiceFormRule, ServiceFormSchema } from "@factory/form/form.rule";
import { serviceFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewService() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreateService } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Service is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addService, isLoading: isCreating } =
    useQyCreateService(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<ServiceFormSchema>({
    defaultValues: serviceFormDefault,
    resolver: zodResolver(ServiceFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: ServiceFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreateService(updatedForm);
    addService(formData);
  };
  const handleValidateError = (err: FieldErrors<ServiceFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
